import { Avatar, Clickable, Col, Divider, Row, Sidepanel, Spinner2, Text } from 'components';
import { Api, theme } from 'lib';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FiArrowLeft, FiPlus } from 'react-icons/fi';
import CreateP4PReplayModal from './components/CreateP4PReplayModal';
import P4PReplayPostPanel from './components/P4PReplayPostPanel';
import PostItem from './components/PostItem';

const POSTS_LIMIT = 50;

const P4PReplayPanel = ({ visible, closeModal, team }) => {
  const [posts, setPosts] = useState([]),
    [loading, setLoading] = useState(true),
    [loadingMore, setLoadingMore] = useState(false),
    [hasMore, setHasMore] = useState(true),
    [createVisible, setCreateVisible] = useState(false),
    [selectedPost, setSelectedPost] = useState(null);

  const scrollRef = useRef(null);

  useEffect(() => {
    if (!visible || !team?._id) return;

    fetchPosts(true);
  }, [visible, team?._id]);

  async function fetchPosts(reset = false) {
    if (reset) {
      setLoading(true);
      setPosts([]);
      setHasMore(true);
    } else {
      setLoadingMore(true);
    }

    const skipPosts = reset ? 0 : posts.length;

    const res = await Api.post('/admin/getPosts', {
      ownerId: team._id,
      type: 'p4p_replay',
      skipPosts,
      skipNewsViews: 0
    });

    if (!res) {
      setLoading(false);
      setLoadingMore(false);
      return;
    }

    const newPosts = res || [];
    setHasMore(newPosts.length >= POSTS_LIMIT);

    if (reset) {
      setPosts(newPosts);
    } else {
      setPosts((prev) => [...prev, ...newPosts]);
    }

    setLoading(false);
    setLoadingMore(false);
  }

  const handleScroll = useCallback(() => {
    if (!scrollRef.current || loadingMore || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    if (scrollHeight - scrollTop - clientHeight < 100) {
      fetchPosts(false);
    }
  }, [loadingMore, hasMore, posts.length]);

  const handlePostClick = (post) => {
    setSelectedPost(post);
  };

  const handleDeletePost = async (post) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;

    const res = await Api.post('/admin/removeData', {
      type: 'post',
      id: post._id
    });

    if (res) {
      fetchPosts(true);
    }
  };

  return (
    <Sidepanel pad="24px 0" wid={800} visible={visible} closeModal={closeModal}>
      <Col>
        <Row pad="0 24px 24px" wid="calc(100% - 48px)" start noFlex>
          <Col center>
            <Clickable onClick={closeModal}>
              <FiArrowLeft size={20} style={{ marginRight: 12 }} />
            </Clickable>
          </Col>

          <Avatar user={team} wid="100%" />

          <Row between wid="100%" ht="100%">
            <Col marg="0 0 0 10px" between ht="100%">
              <Col noFlex>
                <Text med>{team?.name}</Text>
                <Text c1 med col={theme.GREY_60}>
                  @{team?.username}
                </Text>
              </Col>

              <Text b2>{posts.length} replay posts</Text>
            </Col>

            <Clickable onClick={() => setCreateVisible(true)}>
              <FiPlus size={18} />
            </Clickable>
          </Row>
        </Row>

        <Divider />

        {loading ? (
          <Col ht="100%" center pad="24px 0">
            <Spinner2 />
          </Col>
        ) : (
          <Col
            ref={scrollRef}
            onScroll={handleScroll}
            pad="24px"
            start
            wid="100%"
            style={{ overflowY: 'auto', maxHeight: 'calc(100vh - 200px)' }}
          >
            {posts.length === 0 ? (
              <Col center pad="40px 0">
                <Text b2 col={theme.GREY_60}>
                  No replay posts found
                </Text>
              </Col>
            ) : (
              <Col noFlex wid="100%" gap="12px">
                {posts.map((post) => (
                  <PostItem key={post._id} item={post} onClick={handlePostClick} onDelete={handleDeletePost} />
                ))}
                {loadingMore && (
                  <Col center pad="16px 0">
                    <Spinner2 />
                  </Col>
                )}
              </Col>
            )}
          </Col>
        )}
      </Col>

      <P4PReplayPostPanel
        post={selectedPost}
        closeModal={() => setSelectedPost(null)}
        setDataId={setSelectedPost}
        onSuccess={() => fetchPosts(true)}
        team={team}
      />

      <CreateP4PReplayModal
        visible={createVisible}
        closeModal={() => setCreateVisible(false)}
        team={team}
        onSuccess={() => fetchPosts(true)}
      />
    </Sidepanel>
  );
};

export default P4PReplayPanel;
